import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Chip,
  LinearProgress,
  useTheme,
  Skeleton
} from '@mui/material';
import { LocalShipping, CheckCircle, Pending } from '@mui/icons-material';
import { useActiveTransferOrders } from '../hooks/useDashboardData';

export const ActiveTransferOrders: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, error } = useActiveTransferOrders(1, 5);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'picking':
        return theme.palette.info.main;
      case 'ready':
        return theme.palette.success.main;
      case 'allocated':
        return theme.palette.warning.main;
      case 'shipping':
        return theme.palette.primary.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getProgressPercentage = (order: any) => {
    if (order.totalRequestedQuantity === 0) return 0;
    return (order.totalPickedQuantity / order.totalRequestedQuantity) * 100;
  };

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping />
            <Typography variant="h4" fontWeight={700}>
              Active Transfer Orders
            </Typography>
          </Box>
        }
        subheader={
          !isLoading && data && (
            <Typography variant="body2" color="text.secondary">
              {data.totalCount} orders in progress
            </Typography>
          )
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Array.from(new Array(3)).map((_, index) => (
              <Box key={index}>
                <Skeleton width="80%" sx={{ mb: 0.5 }} />
                <Skeleton width="60%" sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 1 }} />
              </Box>
            ))}
          </Box>
        ) : error ? (
          <Typography color="error">Failed to load data</Typography>
        ) : data && data.transferOrders && Array.isArray(data.transferOrders) && data.transferOrders.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {data.transferOrders.map((order) => {
              const progress = getProgressPercentage(order);
              return (
                <Box
                  key={order.transferOrderId}
                  sx={{
                    p: 2,
                    borderRadius: theme.general.borderRadiusSm,
                    backgroundColor: theme.colors.alpha.black[5],
                    '&:hover': {
                      backgroundColor: theme.colors.alpha.black[10]
                    }
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {order.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.clientName}
                      </Typography>
                    </Box>
                    <Chip
                      label={order.status}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(order.status),
                        color: '#fff',
                        fontWeight: 600,
                        ml: 1
                      }}
                    />
                  </Box>

                  {/* Progress Bar */}
                  <Box sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 0.5
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Picking Progress
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {order.totalPickedQuantity.toLocaleString()} / {order.totalRequestedQuantity.toLocaleString()}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(progress, 100)}
                      sx={{
                        height: 6,
                        borderRadius: theme.general.borderRadiusSm,
                        backgroundColor: theme.colors.alpha.black[10],
                        '& .MuiLinearProgress-bar': {
                          borderRadius: theme.general.borderRadiusSm,
                          backgroundColor: getStatusColor(order.status)
                        }
                      }}
                    />
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {order.totalItems} items • {order.deliveryAreaStorageUnitCode}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Updated: {new Date(order.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            {data.totalCount > 5 && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ textAlign: 'center', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                View all {data.totalCount} orders
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircle sx={{ fontSize: '3rem', color: theme.palette.success.main, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No active transfer orders
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
